import { unsafeCoerce } from 'fp-ts/lib/function';
import {
  useContext,
  useEffect,
  createContext,
  useReducer,
  ReactNode,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useUserData } from '../../common/UserProvider/UserProvider';
import { useRouter } from '../../common/hooks/useRouter';
import { useBrowser } from '../../common/services/BrowserService';
import { returnVoid } from '../../common/util/returnVoid';
import { FormState } from './FormState';
import { formStateReducer, FormStateReducerProps, SimpleAction } from './formStateReducer';

export const defaultState: FormState = {
  appointment: {},
  patient: {
    whoShouldReceiveCommunications: 'PATIENT',
  },
  paymentMethod: {},
  user: {},
  meta: {
    currentStep: 0,
    confirmedBookings: [],
    usingKeyboard: false,
  },
  referrals: [],
};

export const FormStateContext = createContext<
  FormStateReducerProps & { saveSessionState: () => void }
>({
  state: defaultState,
  dispatch: returnVoid,
  saveSessionState: returnVoid,
});

export const useFormState = () => useContext(FormStateContext);

export interface FormStateProviderProps {
  children?: ReactNode;
  initialState?: Partial<FormState>;
}

export const FormStateProvider = ({ children, initialState }: FormStateProviderProps) => {
  const { state: parentState } = useFormState();
  const { window } = useBrowser();
  const [formKey, setFormKey] = useState<string>();
  const router = useRouter();

  const configuredState = {
    ...defaultState,
    ...parentState,
    ...initialState,
  };

  const [state, dispatch] = useReducer(formStateReducer, configuredState);

  const saveSessionState = useCallback(() => {
    if (window && window.sessionStorage && formKey) {
      window.sessionStorage.setItem(`form:${formKey}`, JSON.stringify(state));
    }
  }, [state, window, formKey]);

  const value = useMemo(() => ({ state, dispatch, saveSessionState }), [state, saveSessionState]);

  const { data: user } = useUserData();

  useEffect(() => {
    if (user) {
      // reset guest flag in case we use it for something other than the login UI
      dispatch({ type: 'update-meta', partial: { userContinuedAsGuest: false } });
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (window && window.sessionStorage && !formKey && router) {
      const formKey = JSON.stringify(router.query);
      const ls = window.sessionStorage.getItem(`form:${formKey}`);

      if (ls) {
        const sessionStorageState = unsafeCoerce<string, FormState>(JSON.parse(ls));

        const configuredState = {
          ...defaultState,
          ...parentState,
          ...initialState,
          ...sessionStorageState,
        };

        dispatch({ type: 'overwrite-state', state: configuredState });
        window.sessionStorage.removeItem(`form:${formKey}`);
      }

      setFormKey(formKey);
    }
  }, [window, dispatch, parentState, initialState, router, formKey]);

  return <FormStateContext.Provider value={value}>{children}</FormStateContext.Provider>;
};

export const useSimpleDispatch = <T extends SimpleAction>(type: T) => {
  const { dispatch } = useFormState();
  return useCallback(() => {
    dispatch({ type });
  }, [dispatch, type]);
};
